import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, X } from 'lucide-react';

import { promotionalOffersService } from '@/services/providers';
import { planQueries } from '@/services/plans/queries';
import { showToast } from '@/utils/toast';
import { toast } from 'sonner';

interface PromotionalOffersManagementProps {
  providerId: number;
  versionId: number;
  regionId?: number;
}

interface PromotionalOfferData {
  id?: number;
  title?: string | null;
  description?: string | null;
  original_price?: number | null;
  promotional_price?: number | null;
  discount_percentage?: number | null;
  discount_type?: string | null;
  custom_discount_type_name?: string | null;
  display_order?: number;
  internal_reference?: string | null;
  eligibility_requirements?: string | null;
  is_recurring?: boolean | null;
  status?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  is_new_customer_only?: boolean | null;
  terms_conditions?: string | null;
}

interface Plan {
  id: number;
  name: string;
  channel_count?: number;
}

export function PromotionalOffersManagement({ 
  providerId, 
  versionId, 
  regionId 
}: PromotionalOffersManagementProps) {
  const [appliesTo, setAppliesTo] = useState<'provider' | 'plan' | 'category' | 'package'>('provider');
  const [selectedPlans, setSelectedPlans] = useState<number[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [providerOffers, setProviderOffers] = useState<PromotionalOfferData[]>([]);
  const [planOffers, setPlanOffers] = useState<Record<number, PromotionalOfferData[]>>({});
  const [providerTemplateSaved, setProviderTemplateSaved] = useState(false);
  const [planOverridesSaved, setPlanOverridesSaved] = useState<Record<number, boolean>>({});
  const [editingOffer, setEditingOffer] = useState<PromotionalOfferData | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPlanOffer, setEditingPlanOffer] = useState<{ planId: number; offer: PromotionalOfferData } | null>(null);
  const [showPlanOfferForm, setShowPlanOfferForm] = useState(false);

  // Load existing data
  useEffect(() => {
    loadData();
  }, [providerId, versionId, regionId]);

  // Auto-switch to plan mode when plan overrides exist (after refresh)
  useEffect(() => {
    if (Object.keys(planOverridesSaved).length > 0 && appliesTo === 'provider') {
      setAppliesTo('plan');
    }
  }, [planOverridesSaved, appliesTo]);

  const loadData = async () => {
    try {
      // Load existing provider offers (multiple offers)
      const offers = await promotionalOffersService.getAllOffers(providerId, regionId, versionId);
      if (offers && offers.length > 0) {
        // Filter provider-level offers
        const providerLevelOffers = offers.filter(offer => 
          offer.entity_type === 'provider' && offer.entity_id === null
        );
        
        setProviderOffers(providerLevelOffers.map(offer => ({
          id: offer.id,
          title: offer.title,
          description: offer.description,
          original_price: offer.original_price,
          promotional_price: offer.promotional_price,
          discount_percentage: offer.discount_percentage,
          discount_type: offer.discount_type,
          custom_discount_type_name: (offer as any).custom_discount_type_name,
          display_order: (offer as any).display_order,
          internal_reference: (offer as any).internal_reference,
          eligibility_requirements: offer.eligibility_requirements,
          is_recurring: offer.is_recurring,
          status: offer.status,
          starts_at: offer.starts_at,
          ends_at: offer.ends_at,
          is_new_customer_only: offer.is_new_customer_only,
          terms_conditions: offer.terms_conditions,
        })));
        
        if (providerLevelOffers.length > 0) {
          setProviderTemplateSaved(true);
        }
      }

      // Load plans
      const plansData = await planQueries.getPlans(providerId, versionId);
      setPlans(plansData || []);

      // Load existing plan overrides from override tables if any
      if (plansData && plansData.length > 0) {
        const planIds = plansData.map((plan: any) => plan.id);
        const planOverrides = await promotionalOffersService.getPlanOverrides(planIds);
        
        if (planOverrides && planOverrides.length > 0) {
          // Group offers by plan
          const planOffersMap: Record<number, PromotionalOfferData[]> = {};
          planOverrides.forEach((offer: any) => {
            if (!planOffersMap[offer.plan_id]) {
              planOffersMap[offer.plan_id] = [];
            }
            planOffersMap[offer.plan_id].push({
              id: offer.id,
              title: offer.title,
              description: offer.description,
              original_price: offer.original_price,
              promotional_price: offer.promotional_price,
              discount_percentage: offer.discount_percentage,
              discount_type: offer.discount_type,
              custom_discount_type_name: (offer as any).custom_discount_type_name,
              display_order: (offer as any).display_order,
              internal_reference: (offer as any).internal_reference,
              eligibility_requirements: offer.eligibility_requirements,
              is_recurring: offer.is_recurring,
              status: offer.status,
              starts_at: offer.starts_at,
              ends_at: offer.ends_at,
              is_new_customer_only: offer.is_new_customer_only,
              terms_conditions: offer.terms_conditions,
            });
          });
          
          setPlanOffers(planOffersMap);
          
          // Mark plans with overrides as saved
          const savedPlans = [...new Set(planOverrides.map((override: any) => override.plan_id))] as number[];
          const savedState: Record<number, boolean> = {};
          savedPlans.forEach((planId: number) => {
            savedState[planId] = true;
          });
          setPlanOverridesSaved(savedState);
          
          // If there are plan overrides, select those plans but keep current mode
          if (savedPlans.length > 0) {
            setSelectedPlans(savedPlans);
          }
        }
      }

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error("Failed to load existing data", {
        style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
      });
    }
  };

  // Copy provider offers to selected plans when plans are selected
  const copyProviderOffersToPlans = (planId: number) => {
    if (providerOffers.length > 0 && !planOffers[planId]) {
      // Copy provider offers to this plan (without IDs to create new records)
      const copiedOffers = providerOffers.map(offer => ({
        ...offer,
        id: undefined // Remove ID so it creates new records for plan
      }));
      
      setPlanOffers(prev => ({
        ...prev,
        [planId]: copiedOffers
      }));
    }
  };

  const handleAddOffer = () => {
    setEditingOffer({
      title: '',
      description: '',
      original_price: null,
      promotional_price: null,
      discount_percentage: null,
      discount_type: 'general',
      eligibility_requirements: '',
      is_recurring: false,
      status: 'time_limited',
      starts_at: null,
      ends_at: null,
      is_new_customer_only: false,
      terms_conditions: ''
    });
    setShowAddForm(true);
  };

  const handleEditOffer = (offer: PromotionalOfferData) => {
    setEditingOffer({ ...offer });
    setShowAddForm(true);
  };

  const handleDeleteOffer = async (offerId: number) => {
    try {
      await promotionalOffersService.deleteOffer(offerId);
      setProviderOffers(prev => prev.filter(offer => offer.id !== offerId));
      toast.success("Offer deleted successfully", {
        style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
      });
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error("Failed to delete offer", {
        style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
      });
    }
  };

  const handleSaveOffer = async (offerData: PromotionalOfferData) => {
    // Validate required fields
    if (!offerData.title || !offerData.discount_type) {
      toast.error("Please fill in Title and Discount Type", {
        style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
      });
      return;
    }

    // Validate custom discount type name when "others" is selected
    if (offerData.discount_type === 'others' && (!offerData.custom_discount_type_name || offerData.custom_discount_type_name.trim() === '')) {
      toast.error("Please enter a custom discount type name when 'Others' is selected", {
        style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
      });
      return;
    }

    // Validate time-limited offers
    if (offerData.status === 'time_limited') {
      if (!offerData.starts_at || !offerData.ends_at) {
        toast.error("Start Date and End Date are required for Time Limited offers", {
          style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
        });
        return;
      }

      // Validate that end date is after start date
      const startDate = new Date(offerData.starts_at);
      const endDate = new Date(offerData.ends_at);
      
      if (endDate <= startDate) {
        toast.error("End Date must be after Start Date", {
          style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
        });
        return;
      }
    }

    try {
      // Process dates - add default time if only date is provided
      let processedStartsAt = offerData.starts_at;
      let processedEndsAt = offerData.ends_at;
      
      if (offerData.status === 'time_limited') {
        // If date doesn't include time, add 12:00:00 AM (00:00:00)
        if (processedStartsAt && !processedStartsAt.includes(':')) {
          processedStartsAt = processedStartsAt + 'T00:00:00';
        }
        if (processedEndsAt && !processedEndsAt.includes(':')) {
          processedEndsAt = processedEndsAt + 'T23:59:59';
        }
      }

      if (offerData.id) {
        // Update existing offer
        const updatedOffer = await promotionalOffersService.updateOffer(offerData.id, {
          title: offerData.title,
          description: offerData.description,
          original_price: offerData.original_price,
          promotional_price: offerData.promotional_price,
          discount_percentage: offerData.discount_percentage,
          discount_type: (offerData.discount_type || 'general') as 'general' | 'student' | 'senior' | 'military' | 'bundle' | 'others',
          custom_discount_type_name: offerData.custom_discount_type_name,
          display_order: offerData.display_order,
          internal_reference: offerData.internal_reference,
          eligibility_requirements: offerData.eligibility_requirements,
          is_recurring: offerData.is_recurring || undefined,
          status: (offerData.status || 'time_limited') as 'ongoing' | 'time_limited',
          starts_at: offerData.status === 'time_limited' ? (processedStartsAt || undefined) : undefined,
          ends_at: offerData.status === 'time_limited' ? (processedEndsAt || undefined) : undefined,
          is_new_customer_only: offerData.is_new_customer_only || undefined,
          terms_conditions: offerData.terms_conditions,
        });
        
        setProviderOffers(prev => prev.map(offer => 
          offer.id === offerData.id ? { ...offer, ...updatedOffer } : offer
        ));
        toast.success("Promotional offer updated successfully", {
          style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
        });
      } else {
        // Create new offer
        const newOffer = await promotionalOffersService.createOffer({
          provider_id: providerId,
          version_id: versionId,
          region_id: regionId || null,
          entity_type: 'provider',
          entity_id: null,
          title: offerData.title || '',
          description: offerData.description,
          original_price: offerData.original_price,
          promotional_price: offerData.promotional_price,
          discount_percentage: offerData.discount_percentage,
          discount_type: (offerData.discount_type || 'general') as 'general' | 'student' | 'senior' | 'military' | 'bundle' | 'others',
          custom_discount_type_name: offerData.custom_discount_type_name,
          display_order: offerData.display_order,
          internal_reference: offerData.internal_reference,
          eligibility_requirements: offerData.eligibility_requirements,
          is_recurring: offerData.is_recurring || false,
          status: (offerData.status || 'time_limited') as 'ongoing' | 'time_limited',
          starts_at: offerData.status === 'time_limited' ? (processedStartsAt || undefined) : undefined,
          ends_at: offerData.status === 'time_limited' ? (processedEndsAt || undefined) : undefined,
          is_new_customer_only: offerData.is_new_customer_only || false,
          terms_conditions: offerData.terms_conditions,
        });
        
        setProviderOffers(prev => [...prev, newOffer]);
        toast.success("Promotional offer added successfully", {
          style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
        });
      }
      
      setShowAddForm(false);
      setEditingOffer(null);
      setProviderTemplateSaved(true);
    } catch (error: any) {
      console.error('Error saving offer:', error);
      
      // Handle specific database errors
      if (error?.code === '23514') {
        if (error.message?.includes('promotional_offers_dates_check')) {
          toast.error("Invalid dates: For Time Limited offers, both Start Date and End Date must be provided and End Date must be after Start Date", {
            style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
          });
        } else {
          toast.error("Database validation failed. Please check your input values.", {
            style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
          });
        }
      } else if (error?.message) {
        toast.error(`Error: ${error.message}`, {
          style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
        });
      } else {
        toast.error("Failed to save promotional offer. Please check all required fields and try again.", {
          style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
        });
      }
    }
  };

  const handleCancelEdit = () => {
    setShowAddForm(false);
    setEditingOffer(null);
  };

  const handleAddPlanOffer = (planId: number) => {
    setEditingPlanOffer({
      planId,
      offer: {
        title: '',
        description: '',
        original_price: null,
        promotional_price: null,
        discount_percentage: null,
        discount_type: 'general',
        eligibility_requirements: '',
        is_recurring: false,
        status: 'time_limited',
        starts_at: null,
        ends_at: null,
        is_new_customer_only: false,
        terms_conditions: ''
      }
    });
    setShowPlanOfferForm(true);
  };

  const handleEditPlanOffer = (planId: number, offer: PromotionalOfferData) => {
    setEditingPlanOffer({ planId, offer: { ...offer } });
    setShowPlanOfferForm(true);
  };

  const handleDeletePlanOffer = async (planId: number, offerId?: number, discountType?: string) => {
    try {
      if (offerId) {
        // Delete override record from database
        await promotionalOffersService.deleteOffer(offerId);
        setPlanOffers(prev => ({
          ...prev,
          [planId]: prev[planId]?.filter(offer => offer.id !== offerId) || []
        }));
      } else {
        // Remove copied offer (no database record to delete)
        setPlanOffers(prev => ({
          ...prev,
          [planId]: prev[planId]?.filter(offer => offer.discount_type !== discountType || offer.id) || []
        }));
      }
      showToast.deleted('Plan offer');
    } catch (error) {
      console.error('Error deleting plan offer:', error);
      showToast.deleteFailed('plan offer');
    }
  };

  const handleSavePlanOffer = async (planId: number, offerData: PromotionalOfferData) => {
    // Validate required fields
    if (!offerData.title || !offerData.discount_type) {
      toast.error("Please fill in Title and Discount Type", {
        style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
      });
      return;
    }

    // Validate custom discount type name when "others" is selected
    if (offerData.discount_type === 'others' && (!offerData.custom_discount_type_name || offerData.custom_discount_type_name.trim() === '')) {
      toast.error("Please enter a custom discount type name when 'Others' is selected", {
        style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
      });
      return;
    }

    // Validate time-limited offers
    if (offerData.status === 'time_limited') {
      if (!offerData.starts_at || !offerData.ends_at) {
        toast.error("Start Date and End Date are required for Time Limited offers", {
          style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
        });
        return;
      }

      // Validate that end date is after start date
      const startDate = new Date(offerData.starts_at);
      const endDate = new Date(offerData.ends_at);
      
      if (endDate <= startDate) {
        toast.error("End Date must be after Start Date", {
          style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
        });
        return;
      }
    }

    try {
      // Process dates - add default time if only date is provided
      let processedStartsAt = offerData.starts_at;
      let processedEndsAt = offerData.ends_at;
      
      if (offerData.status === 'time_limited') {
        // If date doesn't include time, add 12:00:00 AM (00:00:00)
        if (processedStartsAt && !processedStartsAt.includes(':')) {
          processedStartsAt = processedStartsAt + 'T00:00:00';
        }
        if (processedEndsAt && !processedEndsAt.includes(':')) {
          processedEndsAt = processedEndsAt + 'T23:59:59';
        }
      }

      if (offerData.id) {
        // Update existing plan offer that already has an override ID
        const result = await promotionalOffersService.upsertPlanOverride(planId, {
          title: offerData.title,
          description: offerData.description,
          original_price: offerData.original_price,
          discount_type: (offerData.discount_type || 'general') as 'general' | 'student' | 'senior' | 'military' | 'bundle' | 'others',
          custom_discount_type_name: offerData.custom_discount_type_name,
          display_order: offerData.display_order,
          internal_reference: offerData.internal_reference,
          promotional_price: offerData.promotional_price,
          discount_percentage: offerData.discount_percentage,
          eligibility_requirements: offerData.eligibility_requirements,
          is_recurring: offerData.is_recurring || undefined,
          status: (offerData.status || 'time_limited') as 'ongoing' | 'time_limited',
          starts_at: offerData.status === 'time_limited' ? processedStartsAt : undefined,
          ends_at: offerData.status === 'time_limited' ? processedEndsAt : undefined,
          is_new_customer_only: offerData.is_new_customer_only || undefined,
          terms_conditions: offerData.terms_conditions,
        });
        
        setPlanOffers(prev => ({
          ...prev,
          [planId]: prev[planId]?.map(offer => 
            offer.id === offerData.id ? { ...offer, ...result } : offer
          ) || []
        }));
        toast.success("Plan offer updated successfully", {
          style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
        });
      } else {
        // Check if this is editing a copied offer (no ID but exists in plan offers)
        const currentPlanOffers = planOffers[planId] || [];
        const existingOfferIndex = currentPlanOffers.findIndex(offer => 
          offer.discount_type === offerData.discount_type && !offer.id
        );
        
        if (existingOfferIndex !== -1) {
          // This is editing a copied offer - create override and replace the copied offer
          const result = await promotionalOffersService.upsertPlanOverride(planId, {
            title: offerData.title,
            description: offerData.description,
            original_price: offerData.original_price,
            promotional_price: offerData.promotional_price,
            discount_percentage: offerData.discount_percentage,
            discount_type: (offerData.discount_type || 'general') as 'general' | 'student' | 'senior' | 'military' | 'bundle',
            eligibility_requirements: offerData.eligibility_requirements,
            is_recurring: offerData.is_recurring || undefined,
            status: (offerData.status || 'time_limited') as 'ongoing' | 'time_limited',
            starts_at: offerData.status === 'time_limited' ? processedStartsAt : undefined,
            ends_at: offerData.status === 'time_limited' ? processedEndsAt : undefined,
            is_new_customer_only: offerData.is_new_customer_only || undefined,
            terms_conditions: offerData.terms_conditions,
          });
          
          // Replace the copied offer with the new override
          const updatedOffers = [...currentPlanOffers];
          updatedOffers[existingOfferIndex] = result;
          
          setPlanOffers(prev => ({
            ...prev,
            [planId]: updatedOffers
          }));
          toast.success('Plan offer override created successfully', {
            style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
          });
        } else {
          // This is adding a completely new plan offer
          const result = await promotionalOffersService.upsertPlanOverride(planId, {
            title: offerData.title,
            description: offerData.description,
            original_price: offerData.original_price,
            promotional_price: offerData.promotional_price,
            discount_percentage: offerData.discount_percentage,
            discount_type: (offerData.discount_type || 'general') as 'general' | 'student' | 'senior' | 'military' | 'bundle',
            eligibility_requirements: offerData.eligibility_requirements,
            is_recurring: offerData.is_recurring || undefined,
            status: (offerData.status || 'time_limited') as 'ongoing' | 'time_limited',
            starts_at: offerData.status === 'time_limited' ? processedStartsAt : undefined,
            ends_at: offerData.status === 'time_limited' ? processedEndsAt : undefined,
            is_new_customer_only: offerData.is_new_customer_only || undefined,
            terms_conditions: offerData.terms_conditions,
          });
          
          setPlanOffers(prev => ({
            ...prev,
            [planId]: [...(prev[planId] || []), result]
          }));
          toast.success('Plan offer added successfully', {
            style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
          });
        }
      }
      
      setShowPlanOfferForm(false);
      setEditingPlanOffer(null);
      setPlanOverridesSaved(prev => ({ ...prev, [planId]: true }));
    } catch (error: any) {
      console.error('Error saving plan offer:', error);
      
      // Handle specific database errors
      if (error?.code === '23514') {
        if (error.message?.includes('promotional_offers_dates_check')) {
          toast.error("Invalid dates: For Time Limited offers, both Start Date and End Date must be provided and End Date must be after Start Date", {
            style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
          });
        } else {
          toast.error("Database validation failed. Please check your input values.", {
            style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
          });
        }
      } else if (error?.message) {
        toast.error(`Error: ${error.message}`, {
          style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
        });
      } else {
        toast.error("Failed to save plan offer. Please check all required fields and try again.", {
          style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
        });
      }
    }
  };

  const handleCancelPlanOfferEdit = () => {
    setShowPlanOfferForm(false);
    setEditingPlanOffer(null);
  };

  const getDiscountTypeLabel = (discountType: string, customTypeName?: string) => {
    const labels: Record<string, string> = {
      'general': 'General',
      'student': 'Student',
      'senior': 'Senior',
      'military': 'Military',
      'bundle': 'Bundle',
      'others': customTypeName || 'Others'
    };
    return labels[discountType] || discountType;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Promotional Offers Management</h2>
          <p className="text-muted-foreground">Configure promotional offers and discounts for this provider.</p>
        </div>
        <Badge variant="outline">
          {appliesTo === 'provider' ? 'Provider Template' : 'Plan Overrides'}
        </Badge>
      </div>

      {/* Applies To Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Applies To</CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            value={appliesTo}
            onValueChange={(value: 'provider' | 'plan' | 'category' | 'package') => setAppliesTo(value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="provider">Provider-wide</SelectItem>
              <SelectItem value="plan">Plan-wise</SelectItem>
              <SelectItem value="category">Category-wise</SelectItem>
              <SelectItem value="package">Package-wise</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Provider Template Component (Always shown) */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Template Configuration</CardTitle>
              <p className="text-sm text-muted-foreground">
                Configure all promotional offers for this provider. Each discount type can be configured separately.
              </p>
            </div>
            {providerTemplateSaved && (
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                ✓ Saved
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Existing Offers List */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Current Offers</h3>
              <Button onClick={handleAddOffer} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Offer
              </Button>
            </div>

            {providerOffers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No offers configured yet.</p>
                <p className="text-sm">Click "Add Offer" to get started.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {providerOffers.map((offer) => (
                  <div key={offer.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline">{getDiscountTypeLabel(offer.discount_type || '', offer.custom_discount_type_name || undefined)}</Badge>
                          <span className="font-semibold">{offer.title}</span>
                          {offer.original_price && offer.promotional_price && (
                            <span className="text-sm text-muted-foreground">
                              ${offer.original_price} → ${offer.promotional_price}
                            </span>
                          )}
                          {offer.discount_percentage && (
                            <Badge variant="secondary">{offer.discount_percentage}% OFF</Badge>
                          )}
                          {offer.is_recurring && <Badge variant="secondary">Recurring</Badge>}
                          {offer.is_new_customer_only && <Badge variant="destructive">New Customers</Badge>}
                          <Badge variant={offer.status === 'ongoing' ? 'default' : 'outline'}>
                            {offer.status === 'ongoing' ? 'Ongoing' : 'Time Limited'}
                          </Badge>
                        </div>
                        {offer.description && (
                          <p className="text-sm text-muted-foreground">{offer.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditOffer(offer)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => offer.id && handleDeleteOffer(offer.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add/Edit Offer Form */}
          {showAddForm && editingOffer && (
            <div className="mt-6 border rounded-lg p-4 bg-muted/50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  {editingOffer.id ? 'Edit Offer' : 'Add New Offer'}
                </h3>
                <Button variant="ghost" size="sm" onClick={handleCancelEdit}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <PromotionalOfferForm
                data={editingOffer}
                onChange={setEditingOffer}
                onSave={handleSaveOffer}
                onCancel={handleCancelEdit}
                disabled={false}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan Template Component (Shown when plan-wise is selected OR when plan overrides exist) */}
      {(appliesTo === 'plan' || Object.keys(planOverridesSaved).length > 0) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Plan Offer Overrides</CardTitle>
              <div className="flex gap-2">
                {selectedPlans.some(planId => planOverridesSaved[planId]) && (
                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                    ✓ Overrides Saved
                  </Badge>
                )}
                {providerTemplateSaved && !selectedPlans.some(planId => planOverridesSaved[planId]) && (
                  <Badge variant="secondary">Editable</Badge>
                )}
                {!providerTemplateSaved && (
                  <Badge variant="outline">Read-only until provider template saved</Badge>
                )}
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              {providerTemplateSaved 
                ? 'Configure plan-specific offer overrides for selected plans.'
                : 'Save the provider template first to enable plan-specific overrides.'
              }
            </p>
            
            {/* Plan Selection Warning */}
            {selectedPlans.length === 0 && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm text-yellow-800 font-medium">
                    ⚠️ Please select at least one plan to configure plan-specific offer overrides
                  </span>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {/* Plan Selection */}
            <div className="mb-6 space-y-2">
              <Label>Select Plans for Override</Label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-md p-3">
                {plans.length === 0 ? (
                  <p className="text-sm text-muted-foreground col-span-2">No plans available</p>
                ) : (
                  plans.map((plan) => (
                    <div key={plan.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`plan-${plan.id}`}
                        checked={selectedPlans.includes(plan.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPlans([...selectedPlans, plan.id]);
                            // Copy provider offers to plan when selected
                            copyProviderOffersToPlans(plan.id);
                          } else {
                            setSelectedPlans(selectedPlans.filter(id => id !== plan.id));
                          }
                        }}
                      />
                      <Label htmlFor={`plan-${plan.id}`} className="text-sm flex items-center gap-2">
                        {plan.name}
                        {planOverridesSaved[plan.id] && (
                          <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                            ✓
                          </Badge>
                        )}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Plan Offer Overrides */}
            {selectedPlans.length > 0 ? (
              <div className="space-y-6">
                {selectedPlans.map(planId => {
                  const plan = plans.find(p => p.id === planId);
                  const planOffersList = planOffers[planId] || [];
                  
                  return (
                    <div key={planId} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="text-lg font-semibold">{plan?.name || `Plan ${planId}`}</h4>
                          <p className="text-sm text-muted-foreground">
                            Plan-specific offer overrides
                          </p>
                        </div>
                        <Button 
                          onClick={() => handleAddPlanOffer(planId)}
                          size="sm"
                          variant="outline"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Plan Offer
                        </Button>
                      </div>

                      {planOffersList.length === 0 ? (
                        <div className="text-center py-4 text-muted-foreground">
                          <p>No plan-specific offers configured.</p>
                          <p className="text-sm">Click "Add Plan Offer" to override provider offers for this plan.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {planOffersList.map((offer) => (
                            <div key={offer.id || offer.discount_type} className="border rounded-lg p-3 bg-muted/30">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Badge variant="outline">{getDiscountTypeLabel(offer.discount_type || '', offer.custom_discount_type_name || undefined)}</Badge>
                                    <span className="font-semibold">{offer.title}</span>
                                    {offer.original_price && offer.promotional_price && (
                                      <span className="text-sm text-muted-foreground">
                                        ${offer.original_price} → ${offer.promotional_price}
                                      </span>
                                    )}
                                    {offer.discount_percentage && (
                                      <Badge variant="secondary">{offer.discount_percentage}% OFF</Badge>
                                    )}
                                    {offer.is_recurring && <Badge variant="secondary">Recurring</Badge>}
                                    {offer.is_new_customer_only && <Badge variant="destructive">New Customers</Badge>}
                                    <Badge variant={offer.status === 'ongoing' ? 'default' : 'outline'}>
                                      {offer.status === 'ongoing' ? 'Ongoing' : 'Time Limited'}
                                    </Badge>
                                    <Badge variant="secondary" className="bg-blue-100 text-blue-800">Plan Override</Badge>
                                  </div>
                                  {offer.description && (
                                    <p className="text-sm text-muted-foreground">{offer.description}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEditPlanOffer(planId, offer)}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDeletePlanOffer(planId, offer.id, offer.discount_type || undefined)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>Select plans above to configure plan-specific offer overrides.</p>
              </div>
            )}

            {/* Plan Offer Form */}
            {showPlanOfferForm && editingPlanOffer && (
              <div className="mt-6 border rounded-lg p-4 bg-muted/50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    {editingPlanOffer.offer.id ? 'Edit Plan Offer' : 'Add Plan Offer'}
                  </h3>
                  <Button variant="ghost" size="sm" onClick={handleCancelPlanOfferEdit}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <PromotionalOfferForm
                  data={editingPlanOffer.offer}
                  onChange={(offer) => setEditingPlanOffer({ ...editingPlanOffer, offer })}
                  onSave={(offer) => handleSavePlanOffer(editingPlanOffer.planId, offer)}
                  onCancel={handleCancelPlanOfferEdit}
                  disabled={false}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface PromotionalOfferFormProps {
  data: PromotionalOfferData;
  onChange: (data: PromotionalOfferData) => void;
  onSave: (data: PromotionalOfferData) => void;
  onCancel: () => void;
  disabled?: boolean;
}

function PromotionalOfferForm({ data, onChange, onSave, onCancel, disabled }: PromotionalOfferFormProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="title">Title</Label>
          <Input
            value={data.title || ''}
            onChange={(e) => onChange({ ...data, title: e.target.value })}
            disabled={disabled}
            placeholder="Promotional offer title"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="discount_type">Discount Type</Label>
          <Select
            value={data.discount_type || ''}
            onValueChange={(value) => onChange({ ...data, discount_type: value })}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select discount type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="general">General</SelectItem>
              <SelectItem value="student">Student</SelectItem>
              <SelectItem value="senior">Senior</SelectItem>
              <SelectItem value="military">Military</SelectItem>
              <SelectItem value="bundle">Bundle</SelectItem>
              <SelectItem value="others">Others (Custom)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Custom Discount Type Name - shown only when "others" is selected */}
        {data.discount_type === 'others' && (
          <div className="space-y-2">
            <Label htmlFor="custom_discount_type_name">Custom Discount Type Name</Label>
            <Input
              id="custom_discount_type_name"
              value={data.custom_discount_type_name || ''}
              onChange={(e) => onChange({ ...data, custom_discount_type_name: e.target.value })}
              disabled={disabled}
              placeholder="Enter custom discount type name (e.g., First Responder, Corporate, etc.)"
            />
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select
            value={data.status || 'time_limited'}
            onValueChange={(value) => onChange({ ...data, status: value })}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ongoing">Ongoing</SelectItem>
              <SelectItem value="time_limited">Time Limited</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          value={data.description || ''}
          onChange={(e) => onChange({ ...data, description: e.target.value })}
          disabled={disabled}
          placeholder="Describe the promotional offer..."
          rows={3}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="original_price">Original Price ($)</Label>
          <Input
            type="number"
            step="0.01"
            value={data.original_price || ''}
            onChange={(e) => onChange({ 
              ...data, 
              original_price: e.target.value ? parseFloat(e.target.value) : null 
            })}
            disabled={disabled}
            placeholder="0.00"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="promotional_price">Promotional Price ($)</Label>
          <Input
            type="number"
            step="0.01"
            value={data.promotional_price || ''}
            onChange={(e) => onChange({ 
              ...data, 
              promotional_price: e.target.value ? parseFloat(e.target.value) : null 
            })}
            disabled={disabled}
            placeholder="0.00"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="discount_percentage">Discount (%)</Label>
          <Input
            type="number"
            min="0"
            max="100"
            value={data.discount_percentage || ''}
            onChange={(e) => onChange({ 
              ...data, 
              discount_percentage: e.target.value ? parseInt(e.target.value) : null 
            })}
            disabled={disabled}
            placeholder="0"
          />
        </div>
      </div>

      {/* Show date fields only when status is time_limited */}
      {data.status === 'time_limited' && (
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Time Limited Offer:</strong> Please specify when this offer starts and ends. Times will default to 12:00 AM (start) and 11:59 PM (end) if not specified.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="starts_at">Start Date & Time <span className="text-red-500">*</span></Label>
              <Input
                type="datetime-local"
                value={data.starts_at || ''}
                onChange={(e) => onChange({ ...data, starts_at: e.target.value || null })}
                disabled={disabled}
                placeholder="Required for time-limited offers"
                className={!data.starts_at && data.status === 'time_limited' ? 'border-red-300 focus:border-red-500' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ends_at">End Date & Time <span className="text-red-500">*</span></Label>
              <Input
                type="datetime-local"
                value={data.ends_at || ''}
                onChange={(e) => onChange({ ...data, ends_at: e.target.value || null })}
                disabled={disabled}
                placeholder="Required for time-limited offers"
                className={!data.ends_at && data.status === 'time_limited' ? 'border-red-300 focus:border-red-500' : ''}
              />
            </div>
          </div>
        </div>
      )}

      {/* Show helpful message for ongoing offers */}
      {data.status === 'ongoing' && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-800">
            <strong>Ongoing Offer:</strong> This offer has no expiration date and will remain active until manually disabled.
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="eligibility_requirements">Eligibility Requirements</Label>
        <Textarea
          value={data.eligibility_requirements || ''}
          onChange={(e) => onChange({ ...data, eligibility_requirements: e.target.value })}
          disabled={disabled}
          placeholder="Who is eligible for this offer..."
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="terms_conditions">Terms & Conditions</Label>
        <Textarea
          value={data.terms_conditions || ''}
          onChange={(e) => onChange({ ...data, terms_conditions: e.target.value })}
          disabled={disabled}
          placeholder="Terms and conditions for this offer..."
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center space-x-2">
          <Switch
            checked={data.is_recurring || false}
            onCheckedChange={(checked) => onChange({ ...data, is_recurring: checked })}
            disabled={disabled}
          />
          <Label>Recurring Offer</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            checked={data.is_new_customer_only || false}
            onCheckedChange={(checked) => onChange({ ...data, is_new_customer_only: checked })}
            disabled={disabled}
          />
          <Label>New Customers Only</Label>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => onSave(data)} disabled={disabled}>
          {data.id ? 'Update Offer' : 'Add Offer'}
        </Button>
      </div>
    </div>
  );
}